import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { FooterMenuIcon } from '@/components/icons/footerMenu';
import { useTaskBottomBarStore } from '@/entities/task/model/stores/task-bottom-bar';
import { useUIStore } from '@/app/model';
import { TaskBottomBar } from '@/features/tasks/task-overlays/TaskBottomBar';
import { useMemo, useState } from 'react';
import MobileBottomNavItem from './MobileBottomNavItem';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { useDepartmentGroups } from '@/entities/department';
import { Check } from 'lucide-react';
import clsx from 'clsx';

export function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMenuOpen, openMenu, closeMenu } = useUIStore();
  const [otdelsOpen, setOtdelsOpen] = useState(false);
  const { groups, isLoading: groupsLoading } = useDepartmentGroups();

  const selectedGroupId = useMemo(() => {
    const match = matchPath('/groups/:id', location.pathname);
    return match?.params.id ?? null;
  }, [location.pathname]);

  const isActive = (path: string) =>
    path === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(path);

  const isTaskView = !!matchPath(
    { path: '/tasks/:id', end: true },
    location.pathname
  );

  const handleMenuClick = () => {
    if (isMenuOpen) {
      closeMenu();
    } else {
      closeMenu();
      openMenu();
    }
  };

  const barVisible = useTaskBottomBarStore((s) => s.isVisible);
  const barPayload = useTaskBottomBarStore((s) => s.payload);

  if (isTaskView) {
    if (!barVisible || !barPayload) {
      return null;
    }
    return <TaskBottomBar {...barPayload} />;
  }

  const sortedGroups = [...groups].sort((a, b) =>
    a.Name.localeCompare(b.Name, 'ru')
  );

  return (
    <div
      className="
        fixed bottom-0 left-0 right-0 z-40
        pl-[34.75px] pr-[10px] pt-[15px]
      "
      style={{
        // Стекло — тот же рецепт, что у мобильной шапки детальных страниц
        // (GlassPageHeader/MobileTimerBar): заказчик попросил ровно такую же
        // прозрачность. Блюр лёгкий (1px) — сильный блюр смазывает контент,
        // проходящий под футером, в сплошную заливку, и на плоском фоне
        // календаря выглядит как непрозрачная плашка, а не стекло.
        background: 'rgba(40, 56, 92, 0.9)',
        backdropFilter: 'blur(1px) saturate(250%)',
        WebkitBackdropFilter: 'blur(1px) saturate(250%)',
        boxShadow: '0 -2px 12px rgba(30, 42, 74, 0.08)',
        paddingBottom: 'max(env(safe-area-inset-bottom), 15px)',
      }}
    >
      <div className="flex items-center justify-between text-white text-[12px] font-normal leading-[100%]">
        <MobileBottomNavItem
          label="Мои"
          active={isActive('/')}
          onClick={() => navigate('/')}
        />

        <DropdownMenu open={otdelsOpen} onOpenChange={setOtdelsOpen}>
          <DropdownMenuTrigger asChild>
            <button
              className={clsx(
                'relative hover:opacity-100 transition',
                isActive('/groups') ? 'opacity-100' : 'opacity-80'
              )}
            >
              Отделы
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="center"
            className="bg-[#0d1f42] border border-white/10 text-white min-w-[160px] z-50"
          >
            {groupsLoading ? (
              <DropdownMenuItem disabled className="text-white/60">
                Загрузка...
              </DropdownMenuItem>
            ) : (
              sortedGroups.map((group) => (
                <DropdownMenuItem
                  key={group.ID}
                  className="cursor-pointer flex items-center justify-between gap-2 text-white focus:bg-white/10 focus:text-white"
                  onSelect={() => navigate(`/groups/${group.ID}`)}
                >
                  {group.Name}
                  {String(group.ID) === selectedGroupId && (
                    <Check className="w-3.5 h-3.5 shrink-0" />
                  )}
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <MobileBottomNavItem
          label="Проект"
          active={isActive('/projects')}
          onClick={() => navigate('/projects')}
        />
        <MobileBottomNavItem
          label="Эйзенхауэр"
          active={location.search.includes('view=eisenhower')}
          onClick={() => navigate('/?view=eisenhower')}
        />

        <button
          onClick={handleMenuClick}
          className="opacity-80 hover:opacity-100 transition"
        >
          <FooterMenuIcon />
        </button>
      </div>
    </div>
  );
}
